import type { JSX } from "react";

import magnisLogoRoundedUrl from "../../../../desktop/icon-rounded.png";
import type {
  DemoSeedPhase,
  DemoSeedProfile,
} from "../../hooks/useDemoSeedStatus";
import { Icon } from "../ui";
import type { IconName } from "../ui/Icon";
import { demoSeedCounterLine } from "./demoSeedSplashText";

interface DemoSeedSplashProps {
  readonly loading: boolean;
  readonly profile: DemoSeedProfile;
  readonly requestError: string | null;
  readonly actionError: string | null;
  readonly actionPending: "retry" | "acknowledge" | "refresh" | null;
  readonly onAcknowledge: () => void;
  readonly onRetry: () => void;
  readonly onRefresh: () => void;
}

const PHASE_LABELS: Readonly<Record<DemoSeedPhase, string>> = {
  loading_checkpoint: "Opening the demo dataset…",
  remapping: "Creating your private copy…",
  building_search_index: "Linking records across channels…",
  validating: "Checking every connection…",
  finalizing: "Finishing your workspace…",
  complete: "Opening your workspace…",
};

const VALUE_CARDS: readonly {
  readonly icon: IconName;
  readonly title: string;
  readonly body: string;
}[] = [
  {
    icon: "users",
    title: "One person, every channel",
    body: "The same human in mail, chat, and social — one record.",
  },
  {
    icon: "repeat-2",
    title: "Work that resumes",
    body: "A task waits for the reply that unblocks it, then continues.",
  },
  {
    icon: "shield-check",
    title: "Nothing sends itself",
    body: "Reads run freely. Anything outgoing waits for you.",
  },
];

const PRIMARY_ACTION_CLASS_NAME =
  "rounded-xl bg-accent px-4 py-3 text-sm font-semibold text-on-bright transition-colors hover:bg-accent-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface disabled:cursor-not-allowed disabled:opacity-50";

export function DemoSeedSplash({
  loading,
  profile,
  requestError,
  actionError,
  actionPending,
  onAcknowledge,
  onRetry,
  onRefresh,
}: DemoSeedSplashProps): JSX.Element | null {
  const visible =
    loading ||
    requestError !== null ||
    profile.status === "seeding" ||
    profile.status === "failed" ||
    (profile.status === "ready" && profile.welcomeRequired);
  if (!visible) return null;

  const failed = profile.status === "failed";
  const ready =
    profile.status === "ready" &&
    profile.datasetVersion !== null &&
    profile.welcomeRequired;
  const progress = profile.progress;
  const phaseLabel =
    progress === null ? "Starting setup…" : PHASE_LABELS[progress.phase];
  const progressPercent =
    progress?.totalItems == null
      ? null
      : progress.totalItems === 0
        ? 100
        : Math.round(
            (progress.completedItems / progress.totalItems) * 100,
          );

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="demo-onboarding-title"
      data-testid="demo-seed-splash"
      className="fixed inset-0 z-50 flex items-center justify-center bg-surface px-5 py-8 text-content"
    >
      <div className="flex w-full max-w-[480px] flex-col items-center text-center">
        <img
          src={magnisLogoRoundedUrl}
          alt="Magnis"
          className="h-12 w-12 rounded-xl"
        />
        <h1
          id="demo-onboarding-title"
          className="mt-5 text-xl font-medium tracking-tight"
        >
          {failed
            ? "We couldn't prepare your demo workspace"
            : ready
              ? "Your demo workspace is ready"
              : "Setting up your demo workspace"}
        </h1>
        {profile.summary === null ? null : (
          <p className="mt-2 max-w-[400px] text-sm leading-5 text-content-secondary">
            The mailbox and chats of {profile.summary.companyName}, a fictional
            company. Here&apos;s what the agent will be able to do with them.
          </p>
        )}

        <div className="mt-6 grid w-full grid-cols-[repeat(auto-fit,minmax(170px,1fr))] gap-2.5 text-left">
          {VALUE_CARDS.map((card) => (
            <div
              key={card.title}
              className="rounded-xl border border-content-tertiary/20 bg-surface-secondary p-3.5"
            >
              <Icon name={card.icon} size={16} className="text-accent" />
              <h2 className="mt-2 text-sm font-medium text-content">
                {card.title}
              </h2>
              <p className="mt-1 text-xs leading-4 text-content-secondary">
                {card.body}
              </p>
            </div>
          ))}
        </div>

        <div className="mt-6 w-full">
          <div
            role="progressbar"
            aria-label="Demo workspace setup progress"
            aria-valuetext={phaseLabel}
            {...(progress?.totalItems == null
              ? {}
              : {
                  "aria-valuemin": 0,
                  "aria-valuemax": progress.totalItems,
                  "aria-valuenow": progress.completedItems,
                })}
            className="h-[3px] overflow-hidden rounded-full bg-content-tertiary/20"
          >
            <div
              // @tested-by: tst_fe_onboarding_018
              // @invariant: reported determinate progress always has a visible fill.
              className={`h-full rounded-full bg-accent transition-[width] duration-300 ${
                progressPercent === null ? "w-1/3 animate-pulse" : ""
              }`}
              style={
                progressPercent === null
                  ? undefined
                  : { width: `${String(progressPercent)}%` }
              }
            />
          </div>
          <p className="mt-2 text-xs leading-5 text-content-secondary">
            {demoSeedCounterLine(profile, phaseLabel)}
          </p>
        </div>

        {requestError !== null ? (
          <p role="alert" className="mt-5 text-sm text-status-error">
            {requestError}
          </p>
        ) : null}
        {actionError !== null ? (
          <p role="alert" className="mt-5 text-sm text-status-error">
            {actionError}
          </p>
        ) : null}

        {requestError !== null || failed || actionError !== null ? (
          <button
            type="button"
            onClick={
              requestError !== null
                ? onRefresh
                : failed
                  ? onRetry
                  : onAcknowledge
            }
            disabled={actionPending !== null}
            className={`mt-5 ${PRIMARY_ACTION_CLASS_NAME}`}
          >
            {actionPending !== null ? "Trying again…" : "Try again"}
          </button>
        ) : null}
        {ready && actionError === null ? (
          <button
            type="button"
            onClick={onAcknowledge}
            disabled={actionPending !== null}
            className={`mt-5 ${PRIMARY_ACTION_CLASS_NAME}`}
          >
            {actionPending === "acknowledge" ? "Opening demo…" : "Explore demo"}
          </button>
        ) : null}
      </div>
    </div>
  );
}
